export type SceneConfigNaming = {
  bodies: { uuid: string; label: string }[];
  actuators: { uuid: string; name: string }[];
  joints: { uuid: string; name: string }[];
  sites: { uuid: string; name: string }[];
  meshes: { uuid: string; name: string }[];
  worldBodies: { uuid: string; name: string }[];
};

export type SceneConfigV1 = {
  type: "critterapp-scene-config";
  schemaVersion: 1;
  createdAtIso: string;
  camera: unknown | null;
  orbit: unknown | null;
  naming: SceneConfigNaming;
  poses?: unknown;
  geomPairings: unknown[];
  visibilityOverrides?: unknown;
  visibilitySettings?: unknown;
  cameraProbe: { savedCameras: unknown[]; notes?: string };
};

const emptyNaming = (): SceneConfigNaming => ({
  bodies: [],
  actuators: [],
  joints: [],
  sites: [],
  meshes: [],
  worldBodies: [],
});

/**
 * Migrates raw scene_config.json content to the current SceneConfigV1 shape.
 * Returns null if the raw value is not a recognizable scene config (e.g. a future
 * unknown version), so the caller can skip applying it rather than corrupt state.
 */
export function migrateSceneConfig(raw: unknown): SceneConfigV1 | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;

  if (obj["type"] && obj["type"] !== "critterapp-scene-config") return null;

  // Accept both field names: older bundles used "version", current format uses "schemaVersion".
  const schemaVersion = Number(obj["schemaVersion"]) || Number(obj["version"]) || 0;

  // v0: produced before the schemaVersion field was added — structurally identical to v1.
  // v1: current format.
  if (schemaVersion === 0 || schemaVersion === 1) {
    const migrated: SceneConfigV1 = {
      type: "critterapp-scene-config",
      schemaVersion: 1,
      createdAtIso:
        typeof obj["createdAtIso"] === "string" ? obj["createdAtIso"] : new Date(0).toISOString(),
      camera: obj["camera"] ?? null,
      orbit: obj["orbit"] ?? null,
      naming: (obj["naming"] as SceneConfigNaming) ?? emptyNaming(),
      geomPairings: Array.isArray(obj["geomPairings"]) ? obj["geomPairings"] : [],
      cameraProbe: (obj["cameraProbe"] as SceneConfigV1["cameraProbe"]) ?? { savedCameras: [] },
    };
    if ("poses" in obj) migrated.poses = obj["poses"];
    if ("visibilityOverrides" in obj) migrated.visibilityOverrides = obj["visibilityOverrides"];
    if ("visibilitySettings" in obj) migrated.visibilitySettings = obj["visibilitySettings"];
    return migrated;
  }

  // Unknown future version — do not apply.
  return null;
}
